import type { ReactNode } from 'react';
import { checklistProgress, type ChecklistStatus } from './checklistState.js';
import { cn } from './cn.js';
import { Icon } from './Icon.js';

export interface ChecklistItem {
  key: string;
  label: ReactNode;
  status: ChecklistStatus;
  /** A segunda linha — "recebido · falta validar", "validado no Detran". */
  detail?: ReactNode;
  /**
   * Levar ao item — na mesa, rolar até o campo e focá-lo. Com ela, a linha
   * vira botão.
   */
  onSelect?: () => void;
}

/**
 * Uma lista do que é preciso, com o contador "1 de 7".
 *
 * **O que falta é uma lista, não uma frase** — decisão da mesa do sinistro
 * (item 14 do README do módulo): "faltam 6 campos" obriga a pessoa a procurar
 * quais; a lista diz, e cada linha leva até o item.
 *
 * O contador sai de `checklistProgress`, e quem precisar da mesma conta fora
 * da lista — o selo do rodapé, o bloqueio do botão — chama a mesma função com
 * os mesmos itens. Ver o docblock dela.
 *
 * `alert` pinta a lista inteira quando a pessoa tentou o ato que ela bloqueia
 * — escolheu avançar com pendência —, e é o único momento em que ela grita.
 */
export function Checklist({
  title,
  items,
  alert = false,
  empty,
}: {
  title: string;
  items: readonly ChecklistItem[];
  alert?: boolean;
  /** A frase de quando não há item. Sem ela, a lista vazia não aparece. */
  empty?: ReactNode;
}) {
  const progress = checklistProgress(items);
  if (items.length === 0 && !empty) return null;
  return (
    <section
      aria-label={title}
      className={cn(
        'rounded-borda border p-3',
        alert && !progress.complete
          ? 'border-alerta bg-alerta-suave'
          : 'border-borda bg-superficie',
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="min-w-0 text-xs font-semibold tracking-wide text-texto-tenue uppercase">
          {title}
        </h3>
        {progress.total > 0 && (
          <span className="shrink-0 text-xs font-semibold whitespace-nowrap text-texto-suave tabular-nums">
            {progress.done} de {progress.total}
          </span>
        )}
      </div>
      {items.length === 0 ? (
        <p className="mt-2 text-sm text-texto-suave">{empty}</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-0.5 text-sm">
          {items.map((item) => (
            <li key={item.key}>
              <ChecklistRow item={item} alert={alert} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ChecklistRow({ item, alert }: { item: ChecklistItem; alert: boolean }) {
  const body = (
    <>
      <ChecklistMarker status={item.status} alert={alert} />
      <span className="sr-only">{STATUS_TEXT[item.status]}:</span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(
            'break-words',
            item.status === 'skipped' && 'text-texto-tenue',
            item.status === 'done' && 'text-texto-suave',
          )}
        >
          {item.label}
        </span>
        {item.detail && (
          <span className="text-xs break-words text-texto-suave">{item.detail}</span>
        )}
      </span>
    </>
  );
  const classes = 'flex w-full items-start gap-2 rounded-borda px-1 py-1 text-left';
  if (!item.onSelect) return <div className={classes}>{body}</div>;
  return (
    <button
      type="button"
      onClick={item.onSelect}
      className={cn(classes, 'hover:bg-superficie-suave active:bg-borda-suave')}
    >
      {body}
    </button>
  );
}

/** O estado por extenso, para quem não vê o marcador. */
const STATUS_TEXT: Record<ChecklistStatus, string> = {
  done: 'cumprido',
  pending: 'falta',
  review: 'falta conferir',
  skipped: 'não se aplica',
};

/** O marcador de cada linha. O texto ao lado é quem diz o estado; a cor reforça. */
function ChecklistMarker({ status, alert }: { status: ChecklistStatus; alert: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full',
        status === 'done' && 'bg-sucesso text-texto-inverso',
        status === 'pending' &&
          (alert ? 'border-[1.5px] border-alerta' : 'border-[1.5px] border-borda-forte'),
        status === 'review' && 'border-[1.5px] border-alerta bg-alerta-suave',
        status === 'skipped' && 'border-[1.5px] border-dashed border-borda',
      )}
    >
      {status === 'done' && <Icon nome="confirmado" size={16} />}
    </span>
  );
}
